import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Checkbox,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardActionArea,
  CardContent,
  Snackbar,
  Alert,
  Avatar,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NoPhotographyOutlinedIcon from "@mui/icons-material/NoPhotographyOutlined";
import { DataGrid } from "@mui/x-data-grid";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import { grey } from "@mui/material/colors";
import { useCustomers } from "../../hooks/useCustomers";
import { useProductsBranch } from "../../hooks/useProductsBranch";
import numeral from "numeral";
import * as services from "../../services/saleng";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const SoManagementCreate = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const today = new Date().toISOString().split("T")[0];

  // --- Data Hooks (must be unconditionally at top) ---
  const { customers, loading: loadingCust } = useCustomers();
  const { products, loading: loadingProd } = useProductsBranch();

  // --- States for SO table ---
  const [rowsData, setRowsData] = useState([]);

  // --- Filters for SO list ---
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ร่าง");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState("");
  const statusOptions = ["เปิด", "สิ้นสุด", "ยกเลิก", "ร่าง"];

  // --- Customer-Car Dialog ---
  const [openCustDialog, setOpenCustDialog] = useState(false);
  const [modalStep, setModalStep] = useState("customer");
  const [selCustomer, setSelCustomer] = useState(null);
  const [selCar, setSelCar] = useState(null);
  const [snackNoCar, setSnackNoCar] = useState(false);

  // --- Product Selection Dialog ---
  const [openProdDialog, setOpenProdDialog] = useState(false);
  const [openClearDialog, setOpenClearDialog] = useState(false);
  const [catFilter, setCatFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [detail, setDetail] = useState("");
  const [detail2, setDetail2] = useState("");

  // --- Derive categories & colors ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const getMinEndDate = (start) =>
    new Date(new Date(start).setDate(new Date(start).getDate() + 1))
      .toISOString()
      .split("T")[0];

  const categories = useMemo(() => {
    const seen = new Set();
    return products
      .map((p) => p.category)
      .filter((cat) => {
        if (seen.has(cat.name)) return false;
        seen.add(cat.name);
        return true;
      });
  }, [products]);

  const catColorMap = useMemo(() => {
    return categories.reduce((m, c, i) => {
      m[c.name] = colorPalette[i % colorPalette.length];
      return m;
    }, {});
  }, [categories, colorPalette]);

  // --- Highlight helper ---
  const highlight = (text, q) => {
    if (!q) return <span>{text}</span>;
    const parts = text.split(new RegExp(`(${q})`, "gi"));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase() ? (
        <span key={i} style={{ color: "#1976d2", fontWeight: "bold" }}>
          {p}
        </span>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  };

  // --- Filtered rows for DataGrid ---
  const filteredRows = useMemo(() => {
    const normSearch = search.trim().toLowerCase();

    return rowsData.filter((r) => {
      const productFilter = r.product?.toLowerCase().includes(normSearch);

      return productFilter;
    });
  }, [rowsData, search]);

  // --- Filtered products for Dialog ---
  const filteredProds = useMemo(() => {
    return products
      .filter((p) => (catFilter ? p.category.name === catFilter : true))
      .filter((p) =>
        searchQuery
          ? p.title.toLowerCase().includes(searchQuery.toLowerCase())
          : true
      );
  }, [products, catFilter, searchQuery]);

  // --- Handlers ---
  const toggleSelect = (id) =>
    setSelectedIds((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
    );
  const selectGroup = () =>
    setSelectedIds((s) => [
      ...new Set([...s, ...filteredProds.map((p) => p.id)]),
    ]);
  const selectAll = () => setSelectedIds(products.map((p) => p.id));
  const clearAll = () => {
    setSelectedIds([]);
    setOpenClearDialog(false);
  };

  const confirmSelect = () => {
    const updatedRows = selectedIds.map((id) => {
      const existing = rowsData.find((r) => r.id === id);
      const p = products.find((x) => x.id === id);

      const baseRow = {
        id: p.id,
        detail: p.subCategory?.name || "",
        product: p.title,
        unit: "กก.",
        unlimitedWeight: false,
        actualWeight: 0,
        weight: p.weight ?? 0,
        shortWeight: 0,
        pricePerUnit: p.pricePerUnit ?? 0,
        predictPrice: 0,
      };

      return existing ? { ...baseRow, ...existing } : baseRow;
    });
    
    setRowsData(updatedRows);
    setOpenProdDialog(false);
    setSelectedIds([]);
    setSearchQuery("");
  };

  const handleUnlimited = (id, chk) =>
    setRowsData((r) =>
      r.map((x) =>
        x.id === id
          ? {
              ...x,
              unlimitedWeight: chk,
              weight: chk ? 0 : x.weight,
              shortWeight: chk ? 0 : x.shortWeight,
              predictPrice: chk ? 0 : x.predictPrice,
            }
          : x
      )
    );

  const processRowUpdate = (newRow) => {
    const updatedRow = {
      ...newRow,
      weight: newRow.weight ?? 0,
      shortWeight: newRow.weight ?? 0,
      predictPrice: newRow.weight * newRow.pricePerUnit ?? 0,
    };

    // อัปเดต state
    setRowsData((prev) =>
      prev.map((r) => (r.id === updatedRow.id ? updatedRow : r))
    );

    return updatedRow; // ต้อง return ค่าใหม่ให้ DataGrid ใช้
  };

  const summaryRow = useMemo(() => {
    return {
      id: "summary",
      detail: "รวม",
      product: "",
      unit: "",
      unlimitedWeight: false,
      actualWeight: null,
      weight: rowsData.reduce((sum, r) => sum + (r.weight ?? 0), 0),
      shortWeight: rowsData.reduce((sum, r) => sum + (r.shortWeight ?? 0), 0),
      pricePerUnit: null,
      predictPrice: rowsData.reduce((sum, r) => sum + (r.predictPrice ?? 0), 0),
      isSummary: true,
    };
  }, [rowsData]);

  const combinedRows = [...filteredRows, summaryRow];

  const handleSave = async () => {
    const errors = [];

    if (!endDate) errors.push("กรุณากรอกวันที่สิ้นสุด");
    if (!selCustomer) errors.push("กรุณาเลือกซัพพลายเออร์");
    if (!rowsData.length) errors.push("กรุณาเลือกสินค้าอย่างน้อย 1 รายการ");
  
    if (errors.length) {
      errors.forEach((err) => toast.error(err));
      return;
    }
    
    try {
      const payload = {
        customerId: selCustomer?.id,
        carId: selCar?.id,
        startDate,
        endDate,
        detail,
        detail2,
        items: rowsData.map((r) => ({
          productId: r.id,
          weight: r.weight,
          actualWeight: r.actualWeight,
          shortWeight: r.shortWeight,
          pricePerUnit: r.pricePerUnit,
          predictPrice: r.predictPrice,
          unlimitedWeight: r.unlimitedWeight,
        })),
      };

      await services.createSaleOrders(payload);
      toast.success("บันทึกสำเร็จ");
      navigate("/so_management");

    } catch (err) {
      toast.error("เกิดข้อผิดพลาดในการบันทึก");
    }
  };

  // --- Early return on load ---
  if (loadingCust || loadingProd) {
    return <LinearProgress />;
  }

  return (
    <>
      <Box p={2}>
        <AppBreadcrumbs />
        <Toaster position="bottom-left" reverseOrder={false} />

        <Grid container spacing={2} mt={2}>
          {/* Sidebar ซัพพลายเออร์ */}
          <Grid item xs={12} md={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">ข้อมูลใบเสนอราคา</Typography>
                <Chip label={statusFilter} color="default" />
              </Box>
              <TextField
                label="รายละเอียด"
                fullWidth
                multiline
                rows={5}
                sx={{ mb: 2 }}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
              />

              {selCustomer ? (
                <Paper
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    mb: 4,
                    bgcolor: grey[200],
                  }}
                >
                  <Avatar
                    src={selCustomer.img}
                    sx={{ width: 50, height: 50, mr: 1 }}
                  >
                    {!selCustomer.img && (
                      <NoPhotographyOutlinedIcon fontSize="large" />
                    )}
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography fontWeight="bold">
                      {selCustomer.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selCar ? `รถทะเบียน ${selCar.license}` : ""}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => {
                      setModalStep("customer");
                      setOpenCustDialog(true);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setSelCustomer(null);
                      setSelCar(null);
                      setModalStep("customer");
                    }}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </Paper>
              ) : (
                <Button
                  fullWidth
                  startIcon={<AddIcon />}
                  size="large"
                  onClick={() => setOpenCustDialog(true)}
                  sx={{
                    mb: 4,
                    bgcolor: grey[300],
                    "&:hover": { bgcolor: grey[400] },
                  }}
                >
                  เลือกซัพพลายเออร์
                </Button>
              )}

              <Grid container spacing={1} mb={2}>
                <Grid item xs={6}>
                  <TextField
                    label="เริ่มต้น"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="สิ้นสุด"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    inputProps={{ min: getMinEndDate(startDate) }}
                  />
                </Grid>
              </Grid>

              <TextField
                label="ข้อความท้ายใบเสนอราคา"
                multiline
                rows={5}
                fullWidth
                value={detail2}
                onChange={(e) => setDetail2(e.target.value)}
              />
            </Paper>
          </Grid>

          {/* Main PO list */}
          <Grid item xs={12} md={9}>
            <Grid container alignItems="center" spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="ค้นหาชื่อสินค้า หรือหมวดหมู่หลัก"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs="auto" md="auto" ml="auto">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedIds(rowsData.map((r) => r.id));
                    setOpenProdDialog(true);
                  }}
                >
                  เพิ่มสินค้า
                </Button>
              </Grid>
            </Grid>

            <Box>
              <DataGrid
                rows={combinedRows}
                rowCount={filteredRows.length}
                paginationMode="server"
                columns={[
                  {
                    field: "detail",
                    headerName: "ประเภท",
                    flex: 0,
                    minWidth: 150,
                  },
                  {
                    field: "product",
                    headerName: "สินค้า",
                    flex: 1,
                    minWidth: 200,
                  },
                  {
                    field: "unit",
                    headerName: "หน่วย",
                    flex: 0,
                    minWidth: 150,
                  },
                  {
                    field: "unlimitedWeight",
                    headerName: "ไม่จำกัด นน.",
                    flex: 0,
                    minWidth: 150,
                    renderCell: (params) => {
                      const { isSummary } = params.row;

                      if (isSummary) {
                        return null;
                      }

                      return (
                        <Checkbox
                          checked={params.value}
                          onChange={(e) =>
                            handleUnlimited(params.id, e.target.checked)
                          }
                        />
                      );
                    },
                  },
                  {
                    field: "weight",
                    headerName: "น้ำหนักจริง / น้ำหนัก SO",
                    type: "number",
                    flex: 0,
                    minWidth: 150,
                    sortable: false,
                    editable: true,
                    renderCell: (params) => {
                      const {
                        actualWeight = 0,
                        weight = 0,
                        unlimitedWeight,
                        isSummary,
                      } = params.row;

                      if (isSummary) {
                        return (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              pr: 1,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {numeral(actualWeight).format("0,0.00")}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontWeight: "bold" }}
                              >
                                /
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {numeral(weight).format("0,0.00")}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            pr: 1,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2">
                              {numeral(actualWeight).format("0,0.00")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              /
                            </Typography>
                            {unlimitedWeight ? (
                              <Typography variant="body2" color="primary">
                                ไม่จำกัด
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="body2" color="primary">
                                  {numeral(weight).format("0,0.00")}
                                </Typography>
                                <EditIcon
                                  sx={{ color: "primary.main", ml: 0.5 }}
                                />
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    },
                  },
                  {
                    field: "shortWeight",
                    headerName: "น้ำหนักขาด",
                    type: "number",
                    flex: 0,
                    minWidth: 150,
                    sortable: false,
                    renderCell: (params) => {
                      const {
                        shortWeight = 0,
                        unlimitedWeight,
                        isSummary,
                      } = params.row;

                      if (isSummary) {
                        return (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              pr: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {numeral(shortWeight).format("0,0.00")}
                            </Typography>
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            pr: 1,
                          }}
                        >
                          {unlimitedWeight ? (
                            <Typography variant="body2">-</Typography>
                          ) : (
                            <Typography variant="body2" color="error">
                              {numeral(shortWeight).format("0,0.00")}
                            </Typography>
                          )}
                        </Box>
                      );
                    },
                  },
                  {
                    field: "pricePerUnit",
                    headerName: "ราคาซื้อ",
                    type: "number",
                    flex: 0,
                    minWidth: 150,
                    sortable: false,
                    editable: true,
                    renderCell: (params) => {
                      const { pricePerUnit = 0, isSummary } = params.row;

                      if (isSummary) {
                        return null;
                      }

                      return (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            pr: 1,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" color="primary">
                              {numeral(pricePerUnit).format("0,0.00")}
                            </Typography>
                            <EditIcon sx={{ color: "primary.main", ml: 0.5 }} />
                          </Box>
                        </Box>
                      );
                    },
                  },
                  {
                    field: "predictPrice",
                    headerName: "ราคา (คาดการณ์)",
                    type: "number",
                    flex: 0,
                    minWidth: 150,
                    sortable: false,
                  },
                  {
                    field: "actions",
                    headerName: "",
                    flex: 0,
                    minWidth: 100,
                    sortable: false,
                    
                    renderCell: (params) => {
                      const { isSummary } = params.row;
                      if (isSummary) return null;
                  
                      return (
                        <IconButton
                          color="error"
                          onClick={() =>
                            setRowsData((prev) => prev.filter((r) => r.id !== params.row.id))
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      );
                    },
                  }
                ]}
                processRowUpdate={processRowUpdate}
                isCellEditable={(params) => {
                  if (params.row.isSummary) return false;
                  if (params.field === "weight") {
                    return !params.row.unlimitedWeight;
                  }
                  return true;
                }}
                getRowClassName={(params) =>
                  params.row.isSummary ? "summary-row" : ""
                }
                sx={{
                  "& .summary-row": {
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                  },
                }}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
              />
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{ minWidth: 120 }}
                  onClick={handleSave}
                >
                  บันทึก
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Dialog: เลือกสินค้า */}
      <Dialog
        open={openProdDialog}
        onClose={() => setOpenProdDialog(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>เลือกสินค้า</DialogTitle>
        <DialogContent sx={{ display: "flex", p: 2 }}>
          {/* Sidebar หมวด */}
          <Paper
            variant="outlined"
            sx={{ width: 240, maxHeight: "70vh", overflowY: "auto", mr: 2 }}
          >
            <Box
              onClick={() => setCatFilter(null)}
              sx={{
                p: 2,
                cursor: "pointer",
                bgcolor: catFilter === null ? "action.selected" : "transparent",
              }}
            >
              ทั้งหมด
            </Box>
            {[
              ...new Map(categories.map((cat) => [cat.name, cat])).values(),
            ].map((cat) => {
              return (
                <Box
                  key={cat.id}
                  onClick={() => setCatFilter(cat.name)}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    bgcolor:
                      catFilter === cat.name
                        ? "action.selected"
                        : "transparent",
                  }}
                >
                  {cat.name}
                </Box>
              );
            })}
          </Paper>

          {/* Main เลือกสินค้า */}
          <Box flex={1} display="flex" flexDirection="column">
            <TextField
              fullWidth
              placeholder="ค้นหาชื่อสินค้า..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box flex={1} overflow="auto">
              <Grid container spacing={2}>
                {filteredProds.map((p) => {
                  const isSel = selectedIds.includes(p.id);
                  return (
                    <Grid item xs={6} sm={4} md={3} key={p.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          borderColor: isSel ? "success.main" : undefined,
                          bgcolor: isSel
                            ? theme.palette.grey[300]
                            : alpha(
                                catColorMap[p.category.name] || grey[200],
                                0.2
                              ),
                        }}
                      >
                        <CardActionArea onClick={() => toggleSelect(p.id)}>
                          <CardContent>
                            <Typography noWrap>
                              {highlight(p.title, searchQuery)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {p.subCategory.name}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Chip
            label={`เลือกแล้ว ${selectedIds.length} ชิ้น`}
            color={selectedIds.length ? "success" : "default"}
          />
          <Button size="large" onClick={selectGroup}>
            เลือกกลุ่ม
          </Button>
          <Button size="large" onClick={selectAll}>
            เลือกทั้งหมด
          </Button>
          <Button size="large" onClick={() => setOpenClearDialog(true)}>
            ล้างทั้งหมด
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={confirmSelect}
            disabled={!selectedIds.length}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: ยืนยันล้าง */}
      <Dialog open={openClearDialog} onClose={() => setOpenClearDialog(false)}>
        <DialogTitle>ยืนยันการล้าง</DialogTitle>
        <DialogContent>
          คุณต้องการล้างการเลือกสินค้าทั้งหมดหรือไม่?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClearDialog(false)}>ยกเลิก</Button>
          <Button color="error" variant="contained" onClick={clearAll}>
            ล้างทั้งหมด
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: เลือกซัพพลายเออร์ → รถ */}
      <Dialog
        open={openCustDialog}
        onClose={() => setOpenCustDialog(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {modalStep === "customer" ? (
            "เลือกซัพพลายเออร์"
          ) : (
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => setModalStep("customer")}>
                <ArrowBackIcon />
              </IconButton>
              <Typography sx={{ ml: 1 }}>
                เลือกรถของ {selCustomer?.name}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {modalStep === "customer" ? (
            <Grid container spacing={2}>
              {customers.map((c) => (
                <Grid item xs={12} sm={6} md={4} key={c.id}>
                  <Card variant="outlined">
                    <CardActionArea
                      onClick={() => {
                        setSelCustomer(c);
                        if (c.cars?.length) {
                          setModalStep("car");
                        } else {
                          setOpenCustDialog(false);
                          setSnackNoCar(true);
                        }
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6">{c.name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {c.personType === "corporate"
                            ? "นิติบุคคล"
                            : "บุคคลธรรมดา"}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {selCustomer?.cars.map((car) => (
                <Grid item xs={12} sm={6} md={4} key={car.id}>
                  <Card variant="outlined">
                    <CardActionArea
                      onClick={() => {
                        setSelCar(car);
                        setOpenCustDialog(false);
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6">{car.license}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {car.type} — {car.province}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar: ซัพพลายเออร์ไม่มีรถ */}
      <Snackbar
        open={snackNoCar}
        autoHideDuration={3000}
        onClose={() => setSnackNoCar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="warning">ซัพพลายเออร์รายนี้ยังไม่มีรถในระบบ</Alert>
      </Snackbar>
    </>
  );
};

export default SoManagementCreate;
